import { Component, OnInit, OnDestroy, inject, effect, signal, computed, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { InventoryExport, File as DepotoFile, ProductMovePack } from 'depoto-core/src/entities'
import { OptionsConfig } from '../../configs'
import { SchemaUtil } from '../../utils'
import { CoreService, NotificationService } from '../../services'
import { DateUtil } from 'depoto-core/src/utils/DateUtil'
import { TranslateService } from '@ngx-translate/core'
import { TitleService } from '../../services'
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental'
import { InventoryExportService } from '../../services/entity-services/inventoryExport.service'
import { updateCachedListQueries } from '../../services/query-cache.service'
import { FormControl, FormGroup } from '@angular/forms'

const INVENTORY_XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const GENERIC_FILE_MIME_TYPES = ['', 'application/octet-stream']

@Component({
  selector: 'app-print-inventory-detail',
  templateUrl: 'print-inventory-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PrintInventoryDetailComponent implements OnDestroy {
  queryClient = inject(QueryClient)
  inventoryExportService = inject(InventoryExportService)
  titleService = inject(TitleService)

  private initialFormValue = signal<any>(null)
  submitted = signal<boolean>(false)
  loading = signal<boolean>(false)
  error = false
  refreshInterval: any = null
  readonly inventoryUploadAccept = `.xlsx,${INVENTORY_XLSX_MIME_TYPE}`

  inventoryExportForm = new FormGroup({
    id: new FormControl({ value: 0, disabled: false }),
    generated: new FormControl({ value: '', disabled: false }),
    approved: new FormControl({ value: '', disabled: false }),
    rejected: new FormControl({ value: '', disabled: false }),
  })

  readonly queryInventoryExport = injectQuery(() => ({
    queryKey: ['inventoryExport', Number(this.route.snapshot.params['id'])],
    // queryFn: async (id?: number) => {
    queryFn: async () => {
      const id = Number(this.route.snapshot.params['id'])
      console.log(id)
      if (id) {
        return this.inventoryExportService.getInventoryExport(id, SchemaUtil.inventoryExport).then(result => {
          this.fillFormValue(result)
          if (!result.generated || (!!result.approved && !result.finished)) {
            this.startRefreshing()
          } else {
            this.stopRefreshing()
          }
          return result
        })
      } else {
        return new InventoryExport()
      }
    },
    onSuccess: data => {
      console.log('onSuccess')
      if (data) {
        this.titleService.updateTitleWithObject(data.id, data.date, 'title.print-inventory-detail-id')
      }
      this.loading.set(false)
    },
    onError: data => {
      console.log('onError')
      this.loading.set(false)
    },
  }))

  updateInventoryExport = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)

      return this.inventoryExportService.updateInventoryExport(
        this.inventoryExportForm.value,
        SchemaUtil.inventoryExport
      )
    },
    onSuccess: async data => {
      this.submitted.set(false)
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.inventoryExportForm.getRawValue()
      this.initialFormValue.set(structuredClone(current)) // todo initialFormValue can be taken from formGroup or query ?
      this.inventoryExportForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('alert.inventory.approved'))
      // Update detail cache for this customer
      this.queryClient.setQueryData(['inventoryExport', Number(data.id)], data)
      // Update all cached list queries beginning with ['customers', ...] so listings reflect the update immediately
      updateCachedListQueries(this.queryClient, ['inventoryExports'], data)

      this.removeInventoryExportListings()

      // this.router.navigate(['/customer', data['id']])
    },
  }))

  refreshExportInventory = injectMutation(() => ({
    mutationFn: () => {
      console.log('refreshExportInventory')
      this.submitted.set(true)

      return this.inventoryExportService
        .refreshExportInventory(this.queryInventoryExport.data(), SchemaUtil.inventoryExport)
        .then(result => {
          console.log(result)
          return result
        })
      // return this.inventoryExportService.refreshExportInventory(this.inventoryExportForm.value, {
      //   id: null,
      //   generated: false,
      //   files: [],
      // }).then(result => {
      //   console.log(result)
      //   return result
      // })
    },
    onSuccess: async data => {
      console.log('onSuccess()')
      this.submitted.set(false)
      this.startRefreshing()
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.inventoryExportForm.getRawValue()
      this.initialFormValue.set(structuredClone(current)) // todo initialFormValue can be taken from formGroup or query ?
      this.inventoryExportForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('alert.inventory.approved'))
      // Update detail cache for this customer
      this.queryClient.setQueryData(['inventoryExport', Number(data.id)], data)
      // Update all cached list queries beginning with ['customers', ...] so listings reflect the update immediately
      updateCachedListQueries(this.queryClient, ['inventoryExport'], data)
      //
      this.removeInventoryExportListings()
    },
  }))

  isCreating = computed(() => this.queryInventoryExport.data()?.id)

  constructor(
    private route: ActivatedRoute,
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {
    effect(() => {
      const d = this.queryInventoryExport.data()
      if (d) {
        this.fillFormValue(d)
      }
    })
  }

  // ngOnInit() {
  // todo can be removed?
  // this.route.params.forEach((params: Params) => {
  //   const id = +params['id']
  //   this.queryInventoryExport.refetch()
  // })
  // }

  ngOnDestroy() {
    this.stopRefreshing()
  }

  handleFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement
    const uploadedFile = input.files?.[0]

    if (uploadedFile && this.queryInventoryExport.data()?.id > 0) {
      if (!this.isValidInventoryUploadFile(uploadedFile)) {
        this.notificationService.error(this.translateService.instant('inventory.file.invalid-type'))
        input.value = ''
        return
      }

      this.loading.set(true)
      this.inventoryExportForm.get('generated')?.setValue(null)
      const reader = new FileReader()
      reader.readAsDataURL(new Blob([uploadedFile]))
      reader.onload = async evt => {
        const filename = uploadedFile.name
        const file: DepotoFile = {
          id: null,
          text: filename,
          originalFilename: filename,
          mimeType: uploadedFile.type,
          base64Data: String(evt.target['result']).replace('data:;base64,', ''),
          main: null,
          url: null,
          size: null,
          product: null,
          order: null,
          thumbnails: [],
        }
        try {
          await this.core.services.file
            .create(file, null, null, null, this.queryInventoryExport.data()?.id)
            .then(() => {
              this.notificationService.success(this.translateService.instant('alert.file.uploaded'))
            })
          await this.queryInventoryExport.refetch()
        } catch (err) {
          console.error(err)
        } finally {
          this.loading.set(false)
        }
      }
      reader.onerror = function () {
        // todo
        // alert(processingError)
      }
    }
  }

  private isValidInventoryUploadFile(file: File): boolean {
    const filename = file.name?.toLowerCase() || ''
    const mimeType = file.type || ''

    if (!filename.endsWith('.xlsx')) {
      return false
    }

    return mimeType === INVENTORY_XLSX_MIME_TYPE || GENERIC_FILE_MIME_TYPES.includes(mimeType)
  }

  async approveInventory() {
    this.loading.set(true)
    try {
      this.inventoryExportForm.get('approved')?.setValue(DateUtil.getDateStringFromUTCString(new Date().toUTCString()))
      this.updateInventoryExport.mutate()
      this.startRefreshing()
    } catch (err) {
      console.error(err)
    } finally {
      this.loading.set(false)
    }
  }

  async rejectInventory() {
    this.loading.set(true)
    this.inventoryExportForm.get('rejected')?.setValue(DateUtil.getDateStringFromUTCString(new Date().toUTCString()))
    this.updateInventoryExport.mutate()
    this.loading.set(false)
  }

  startRefreshing(): void {
    if (!this.refreshInterval) {
      this.refreshInterval = setInterval(() => {
        this.queryInventoryExport.refetch()
      }, OptionsConfig.strings.refreshIntervalTimeMS)
    }
  }

  stopRefreshing(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  }

  downloadGenerated(): void {
    const inventoryExport = this.queryInventoryExport.data()
    if (inventoryExport?.generated?.length > 0 && inventoryExport?.url?.length > 0) {
      this.downloadDocument(inventoryExport.url)
    }
  }

  downloadGeneratedFinal(): void {
    const inventoryExport = this.queryInventoryExport.data()
    const files = inventoryExport?.files
    if (inventoryExport?.generated?.length > 0 && files?.length > 0) {
      this.downloadDocument(files[files.length - 1].url)
    }
  }

  downloadProductMovePack(pmp: ProductMovePack | Partial<ProductMovePack>): void {
    this.downloadDocument(pmp?.url)
  }

  private downloadDocument(url?: string | null): void {
    if (url?.length > 0) {
      this.core.services.download.downloadDocumentAndShowInNewWindow(url)
    }
  }

  isGeneratedFinal(): boolean {
    return (
      this.queryInventoryExport.data().files &&
      this.queryInventoryExport.data().files.length > 0 &&
      this.queryInventoryExport.data().generated &&
      this.queryInventoryExport.data().generated.length > 0
    )
  }

  isGeneratedPmps(data = this.queryInventoryExport.data()): boolean {
    return !(!!data.approved && !data.finished)
  }

  public fillFormValue(inventoryExport) {
    const value = {
      id: inventoryExport.id || 0,
      generated: inventoryExport.generated || '',
      approved: inventoryExport.approved || '',
      rejected: inventoryExport.rejected || '',
    }

    this.inventoryExportForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.inventoryExportForm.markAsPristine()
  }

  private removeInventoryExportListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['inventoryExport'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }
}
